import React, { useState, useEffect } from "react";
import { Loader2 } from "lucide-react";
import { Pagination } from "../../components/ui/Pagination";
import { Modal, ModalContent } from "../../components/ui/Modal";
import { ReviewEventLink } from "../../components/ui/DuplicateReview";
import { eventsApi } from "../../utils/api";
import { plural } from "../../utils/helpers";

const PAGE_SIZE = 20;
const NO_RESULT = { events: [], pagination: null, error: null };

/**
 * The events carrying one theme — opened from an event count on the Themes page.
 *
 * Rows reuse `ReviewEventLink` rather than rendering their own: it already gives the admin deep
 * link, the consumer link, and the date/status meta line, and it is what the duplicate-review
 * queues use for the same job.
 *
 * **The count on the page and the total here deliberately differ.** The column counts *active*
 * events (`THEME_USAGE_SCOPE` on the API side), because that is what the recommender can actually
 * rank. This list is unfiltered, so it also shows pending, hidden and duplicate events — which is
 * usually the reason an admin opened it. The header says so rather than leaving two numbers to
 * disagree silently.
 */
export function ThemeEventsModal({ theme, onClose }) {
  const [result, setResult] = useState(NO_RESULT);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);

  const themeId = theme?.id;

  // Closing clears `theme`, so the next open — of any theme — starts on page 1 with nothing from
  // the last theme left over. The spinner only covers the list: without clearing, the header's
  // total and the pager would show the previous theme's numbers for the whole request.
  useEffect(() => {
    setPage(1);
    setResult(NO_RESULT);
  }, [themeId]);

  useEffect(() => {
    if (!themeId) return undefined;
    // Only the latest request may write: paging quickly can land responses out of order.
    let cancelled = false;
    setLoading(true);
    eventsApi
      .getAll({ theme_id: themeId, page, page_size: PAGE_SIZE })
      .then((data) => {
        if (!cancelled) setResult({ events: data.events || [], pagination: data.pagination || null, error: null });
      })
      .catch((e) => {
        // Said inside the modal: the page's banner sits behind its overlay.
        const error = e.response?.data?.detail || "Failed to load the theme's events";
        if (!cancelled) setResult({ events: [], pagination: null, error });
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [themeId, page]);

  const { events, pagination, error } = result;
  const total = pagination?.total;
  const active = theme?.usage;

  return (
    <Modal open={theme != null} onOpenChange={(open) => !open && onClose()}>
      <ModalContent
        title={theme?.name || "Events"}
        description={
          total == null
            ? undefined
            : `${plural(total, "event")} carry this theme` +
              (active != null && active !== total ? ` — ${active.toLocaleString()} of them active` : "")
        }
      >
        {loading ? (
          <div className="flex justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : error ? (
          <p className="py-8 text-center text-sm text-red-700">{error}</p>
        ) : events.length === 0 ? (
          <p className="py-8 text-center text-sm text-muted-foreground">No events carry this theme.</p>
        ) : (
          <ul className="space-y-2">
            {events.map((event) => (
              <ReviewEventLink key={event.id} event={event} />
            ))}
          </ul>
        )}

        {pagination && pagination.total_pages > 1 && (
          <div className="pt-3">
            <Pagination page={page} totalPages={pagination.total_pages} onPageChange={setPage} />
          </div>
        )}
      </ModalContent>
    </Modal>
  );
}

/**
 * An event count that opens the list behind it. Renders as plain text at zero, because there is
 * nothing to open and a button that does nothing is worse than a number.
 */
export function EventCountButton({ usage, onOpen, label }) {
  if (!usage) return <span className="text-muted-foreground">{plural(0, "event")}</span>;
  return (
    <button
      type="button"
      onClick={onOpen}
      aria-label={label}
      className="rounded text-primary underline-offset-2 hover:underline focus:outline-none focus:ring-2 focus:ring-ring"
    >
      {plural(usage, "event")}
    </button>
  );
}
